import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth, getPendingReferralCode, clearPendingReferralCode } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Synchronous guard — prevents double-execution under StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      // Two supported flows land here:
      //   1. Direct Google OAuth → backend already set the session cookie and
      //      bounced us back to /auth/callback?ok=1. Just refresh /auth/me.
      //   2. (Legacy) Emergent Auth → URL fragment `#session_id=...` that we
      //      exchange for a session cookie via /api/auth/session.
      const url = new URL(window.location.href);
      const okFlag = url.searchParams.get('ok') === '1';
      const hash = window.location.hash || '';
      const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
      const sessionId = hashParams.get('session_id');

      // Strip query/fragment so refreshing the page doesn't re-trigger
      try { window.history.replaceState({}, '', window.location.pathname); } catch (_) {}

      if (sessionId) {
        // Legacy Emergent path
        const referralCode = getPendingReferralCode();
        try {
          await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId, referral_code: referralCode },
            { withCredentials: true }
          );
          if (referralCode) clearPendingReferralCode();
        } catch (err) {
          console.error('Auth exchange failed', err);
          navigate('/?auth_error=1', { replace: true });
          return;
        }
      } else if (!okFlag) {
        // Neither hash nor ?ok=1 — nothing to do; bounce home
        navigate('/');
        return;
      }

      // Google OAuth path already set the cookie server-side; just rehydrate
      // the user from /auth/me and route to the builder.
      try {
        clearPendingReferralCode();
        await refresh();
        navigate('/build', { replace: true });
      } catch (err) {
        console.error('Auth refresh failed', err);
        navigate('/?auth_error=1', { replace: true });
      }
    };
    run();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex items-center gap-3 text-neutral-700">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Signing you in…</span>
      </div>
    </div>
  );
}
